import {updateFactory} from '@modifi/redux'
import {
  BankAccountView,
  ExistingBankAccountView,
  getAuthenticatedThemisClient,
} from '@modifi/onboarding-api-clients'
import {aUserFeedBackSubmitFailed, aUserFeedBackSubmitSuccessful} from '@modifi/plugin-onboarding'
import {difference} from 'ramda'
import {aPush} from '@modifi/router'
import {Currency} from '@modifi/enums'
import {mapSellerBankAccountToRequest, omitDocuments, pickDocuments} from '../utils'
import {
  aAppendSellerBankAccountToCompanyInLocalStore,
  aFetchSellerBankAccounts,
  aUpdateLocalSellerBankAccount,
} from '../../../../data/seller-bank-accounts'
import {SECTION_BANK_DETAILS} from '../../../../constants/sections'
import {
  aFetchFinancingOptions,
  aRemoveSellerBankAccountFromLocalFinancingOption,
  aUpdateLocalFinancingOptionSellerBankAccount,
} from '../../../../data/financing-options'
import {
  aFetchCompanyDocuments,
  aUpdateCompanyDocuments,
  mergeDocumentChanges,
} from '../../../../data/company-documents'
import {ROUTE_COMPANY_PROFILE_BANK_DETAILS} from '../../../../routes/constants'
import {selectCurrentCompaniesCountryCodeLowerCased} from '../../../../data/company'
import {getLogger} from '../../../../utils'
import {
  selectAssignedCurrenciesByExternalBankAccountId,
  selectAvailableSellerBankAccountCurrencies,
} from './form'
import {aFetchInputFields} from '../../../../data/input-fields'
import {aClearBankUtils} from '../../../../data/bank-utils'
import {AppThunkAction} from '../../../../types/redux'

const logger = getLogger('seller-bank-account-page')
const apiClient = getAuthenticatedThemisClient()

const {reducer, actions, selectors} = updateFactory({
  rootSelector: state => state?.pages?.sellerBankAccounts?.update,
  actionNamePrefix: 'pages/seller-bank-account/update',
  submitRequest:
    (a, data: Record<string, any>, externalBankAccountId?: string) =>
    async (dispatch, getState): Promise<void> => {
      logger.info(`Submitting seller bank account page`)
      logger.debug('changes: %o', data)
      const countryCode = selectCurrentCompaniesCountryCodeLowerCased(getState())

      const documentChanges = pickDocuments(data)
      const mappedData = mapSellerBankAccountToRequest(omitDocuments(data)) as {
        currency?: string[] | string
        intermediaryBankCode?: string
        intermediaryBankName?: string
      } & BankAccountView

      if (mappedData?.intermediaryBankCode || mappedData?.intermediaryBankCode === '') {
        if (mappedData.intermediaryBank) {
          mappedData.intermediaryBank.bankCode = mappedData?.intermediaryBankCode
        } else {
          mappedData.intermediaryBank = {
            bankCode: mappedData?.intermediaryBankCode,
          }
        }
        delete mappedData.intermediaryBankCode
      }
      // currency workaround - when all currencies are removed the form returns a empty string instead of empty array
      let {currency, ...requestData} = {
        ...mappedData,
        currency:
          typeof mappedData.currency === 'string'
            ? [mappedData.currency].filter(Boolean)
            : mappedData.currency,
      }

      let createdNewBankAccount = false
      let updatedBankAccount: ExistingBankAccountView
      let currenciesToBeRemoved: string[] = []
      let previousCurrencies: string[] = []
      let availableCurrenciesOptions: {label: string; value: string}[] = []

      if (currency) {
        if (externalBankAccountId) {
          availableCurrenciesOptions = selectAvailableSellerBankAccountCurrencies(
            getState(),
            externalBankAccountId
          )
          previousCurrencies = selectAssignedCurrenciesByExternalBankAccountId(
            getState(),
            externalBankAccountId
          )
        }
        currenciesToBeRemoved = difference(previousCurrencies, currency)
      }
      const uSDExternalId =
        availableCurrenciesOptions.find(currencyOption => currencyOption?.label === Currency.USD)
          ?.value || ''
      if (currenciesToBeRemoved.includes(uSDExternalId)) {
        requestData = {
          ...requestData,
          intermediaryBank: {
            bankCode: '',
          },
        }
      }

      dispatch(a.pendingAction())

      try {
        if (externalBankAccountId) {
          logger.info(
            `Updating existing seller bank account externalBankAccountId=%s`,
            externalBankAccountId
          )

          updatedBankAccount = await apiClient.SellerBankAccounts.update({
            externalBankAccountId,
            body: requestData,
          }).then(res => res.data())
          dispatch(
            aUpdateLocalSellerBankAccount({
              externalId: externalBankAccountId,
              ...updatedBankAccount,
            })
          )
        } else {
          logger.info(`Creating new seller bank account`)

          updatedBankAccount = await apiClient.SellerBankAccounts.create({
            body: requestData,
          }).then(res => res.data())

          dispatch(aAppendSellerBankAccountToCompanyInLocalStore(updatedBankAccount))

          createdNewBankAccount = true
        }
      } catch (error: any) {
        dispatch(a.errorAction(error))
        dispatch(aUserFeedBackSubmitFailed(SECTION_BANK_DETAILS, countryCode ?? undefined))
        return
      } finally {
        dispatch(aFetchSellerBankAccounts())
      }

      if (currency) {
        if (externalBankAccountId) {
          previousCurrencies = selectAssignedCurrenciesByExternalBankAccountId(
            getState(),
            externalBankAccountId
          )
        }

        const currenciesToBeAdded = difference(currency, previousCurrencies)

        const removePromises = currenciesToBeRemoved.map(externalCompanyFinancingId => {
          dispatch(aRemoveSellerBankAccountFromLocalFinancingOption(externalCompanyFinancingId))
          return apiClient.SellerBankAccounts.unlinkFromFinancingConfig({
            externalCompanyFinancingId,
            externalBankAccountId: externalBankAccountId!,
          })
        })
        const addPromises = currenciesToBeAdded.map(externalCompanyFinancingId => {
          dispatch(
            aUpdateLocalFinancingOptionSellerBankAccount(
              externalCompanyFinancingId,
              updatedBankAccount
            )
          )
          return apiClient.SellerBankAccounts.linkToFinancingConfig({
            externalCompanyFinancingId,
            body: {
              externalBankAccountId: updatedBankAccount.externalId,
            },
          })
        })

        try {
          await Promise.all([...removePromises, ...addPromises])
        } catch (error: any) {
          dispatch(a.errorAction(error))
          dispatch(aUserFeedBackSubmitFailed(SECTION_BANK_DETAILS, countryCode ?? undefined))
          return
        } finally {
          dispatch(aFetchFinancingOptions())
        }
      }

      if (documentChanges) {
        try {
          await dispatch(aUpdateCompanyDocuments(mergeDocumentChanges(documentChanges)))
        } catch (error: any) {
          dispatch(a.errorAction(error))
          return
        } finally {
          dispatch(aFetchCompanyDocuments())
        }
      }

      if (createdNewBankAccount && updatedBankAccount.externalId) {
        dispatch(aPush(ROUTE_COMPANY_PROFILE_BANK_DETAILS))
      }
      dispatch(aFetchInputFields())
      dispatch(a.successAction())

      dispatch(aUserFeedBackSubmitSuccessful(SECTION_BANK_DETAILS, countryCode ?? undefined))
    },
})

export default reducer

export const {
  selectUpdatePending: selectSubmitInProgress,
  selectUpdateHasError: selectSubmitHasError,
  selectUpdateErrorMessage: selectSubmitErrorMessage,

  selectUpdateErrorFieldErrors: selectSubmitErrorFieldErrors,
} = selectors

export const {aSubmit: aSavePage} = actions

export const aDiscardChanges =
  (): AppThunkAction<void> =>
  (dispatch): void => {
    // @ts-expect-error to be fixed
    dispatch(actions?.aReset())
    dispatch(aClearBankUtils())
  }
